/**
 * useAuth.js
 * Custom hook for authentication state and actions
 *
 * Responsibilities:
 * - Check session on mount (call GET /auth/me)
 * - Provide logout function (call POST /auth/logout + redirect)
 * - Expose auth state from Zustand store
 *
 * Usage:
 *   const { user, isAuthenticated, isLoading, checkSession, logout } = useAuth();
 */

import { useEffect } from 'react';
import useAuthStore from '../store/authStore';
import { clearAttempts as clearSignInAttempts, hadRecentAttempt } from '../utils/signInThrottle';
import { track } from '../utils/authTelemetry';
import { catalystFetch } from '../services/catalystFetch';

const SESSION_CHECK_TIMEOUT_MS = 15000; // /auth/me must answer in time, else app would hang

// Track if session has been checked globally (across all hook instances)
let sessionChecked = false;

const API_BASE = process.env.NODE_ENV === 'production'
  ? '/server/processMedia'
  : '';

export default function useAuth() {
  const { user, isAuthenticated, isLoading, setUser, clearUser, setLoading } = useAuthStore();

  /**
   * Check if user has an active session
   * Called automatically on mount
   */
  // Distinguish kinds of "not authenticated" so we can SEE how users hit Zoho's limit:
  // - stale session (localStorage said authed, server says no) — the suspected cause
  // - didn't stick (just attempted sign-in, still 401) — session not establishing
  const handleNotAuthenticated = () => {
    const wasAuthed = useAuthStore.getState().isAuthenticated;
    if (wasAuthed) track('AuthStaleSession');
    if (hadRecentAttempt()) track('SignInDidNotStick');
    clearUser();
    try { localStorage.removeItem('speech-coach-auth'); } catch (_) { /* noop */ }
  };

  const checkSession = async () => {
    sessionChecked = true;
    setLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SESSION_CHECK_TIMEOUT_MS);
    try {
      const response = await catalystFetch(`${API_BASE}/auth/me`, {
        method: 'GET',
        credentials: 'include', // Required for HTTPOnly cookies
        signal: controller.signal,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUser(data.user);
          clearSignInAttempts(); // authenticated — reset the sign-in throttle guard
        } else {
          handleNotAuthenticated();
        }
      } else if (response.status === 401) {
        handleNotAuthenticated();
      } else {
        console.error('[useAuth] Session check failed:', response.status);
        clearUser();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // /auth/me hung — log it; otherwise the app sits on a spinner and the user
        // reloads, which spawns fresh sign-in attempts toward the 10/10min limit.
        console.error('[useAuth] Session check timed out');
        track('AuthCheckTimeout');
      } else {
        console.error('[useAuth] Session check error:', error);
      }
      clearUser();
    } finally {
      clearTimeout(timeoutId);
      // Hard safety net: the app renders the "One moment." loader while isLoading is
      // true. setUser/clearUser already reset it, but if any path above is bypassed we
      // must never leave the app frozen on the loader — always resolve loading here.
      setLoading(false);
    }
  };

  /**
   * Logout. The bug we kept hitting: the Catalyst APP session (the cookie /auth/me
   * validates) was never cleared, so returning to the app silently re-authenticated the
   * user even after the Zoho account was logged out.
   *
   * Fix: catalyst.auth.signOut(redirectURL) routes through Catalyst's own logout
   * endpoint (/accounts/p/<zaid>/logout) — which clears the Catalyst session — and then
   * lands on redirectURL. IMPORTANT: the SDK appends redirectURL as `serviceurl` WITHOUT
   * url-encoding it, so it MUST be a clean URL (no ?/&) or the request is malformed (400).
   * We pass the bare app URL; with the session cleared, that page shows sign-in.
   * Fallback (SDK missing): the Zoho .in account logout (AUTH.md Rule 3 — India DC).
   */
  const logout = async () => {
    // Clear local state first
    clearUser();
    sessionChecked = false;
    localStorage.removeItem('speech-coach-auth');
    // Drop the recent-attempt hint too, so the post-logout load has no session hint
    // → getCatalystAuthToken() is skipped → signed-out screen is instant and clean.
    clearSignInAttempts();

    // Call backend to clear Catalyst session and get Zoho logout URL
    try {
      await catalystFetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('[useAuth] Backend logout error:', error);
    }

    const redirectUrl = encodeURIComponent(window.location.origin + '/app/?logged_out=1');
    const zohoLogoutUrl = `https://accounts.zoho.in/logout?servicename=ZohoCatalyst&serviceurl=${redirectUrl}`;

    // Pass a CLEAN app URL (no query string) — the SDK does not url-encode the redirect,
    // so a URL with ?/& produces a malformed /accounts/p/<zaid>/logout request (400).
    if (window.catalyst?.auth && typeof window.catalyst.auth.signOut === 'function') {
      try {
        window.catalyst.auth.signOut(`${window.location.origin}/app/`);
        return;
      } catch (err) {
        console.error('[useAuth] catalyst.auth.signOut failed, falling back to redirect:', err);
      }
    }
    window.location.href = zohoLogoutUrl;
  };

  /**
   * Check session on mount - only once across all hook instances
   */
  useEffect(() => {
    if (!sessionChecked) {
      sessionChecked = true;
      checkSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  return {
    user,
    isAuthenticated,
    isLoading,
    checkSession,
    logout,
  };
}
